import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Circle, Camera, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SopCompletion, getSopCompletion, updateSopStep, finalizeSopCompletion } from '../services/sopApi';
import { useAuth } from '../hooks/useAuth';

export default function SopCompletionPage() {
    const { completionId } = useParams<{ completionId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    
    // Get job card information from navigation state
    const jobCardInfo = location.state as {
        jobCardId?: number;
        jobNumber?: string;
        jobcardStoreNumber?: string;
        jobcardStoreName?: string;
    } | null;
    
    // Helper function to navigate back to job card or job cards list
    const navigateBack = () => {
        if (jobCardInfo?.jobCardId) {
            navigate(`/jobcards/${jobCardInfo.jobCardId}/edit`);
        } else {
            navigate('/jobcards');
        }
    };
    
    const [completion, setCompletion] = useState<SopCompletion | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [signature, setSignature] = useState('');
    const [finalNotes, setFinalNotes] = useState('');

    useEffect(() => {
        const fetchCompletion = async () => {
            if (!completionId) return;
            
            try {
                const response = await getSopCompletion(parseInt(completionId));
                console.log('SOP Completion Data:', response.data);
                console.log('SOP Steps:', response.data.sop?.steps);
                console.log('Completion Steps:', response.data.completion_steps);
                setCompletion(response.data);
            } catch (error) {
                console.error('Error fetching completion:', error);
                toast.error('Failed to load SOP completion');
                navigateBack();
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompletion();
    }, [completionId, navigate]);

    const updateStepCompletion = async (stepId: number, isCompleted: boolean, comment?: string) => {
        if (!completion) return;

        try {
            const updatedSteps = completion.completion_steps.map(step => 
                step.id === stepId 
                    ? { ...step, is_completed: isCompleted, comment: comment || step.comment }
                    : step
            );

            setCompletion({
                ...completion,
                completion_steps: updatedSteps
            });

            await updateSopStep(stepId, { is_completed: isCompleted, comment });
            
        } catch (error) {
            console.error('Error updating step:', error);
            toast.error('Failed to update step');
            
            // Revert the change on error
            if (completionId) {
                const response = await getSopCompletion(parseInt(completionId));
                setCompletion(response.data);
            }
        }
    };

    const handleStepToggle = (stepId: number, currentStatus: boolean, comment?: string) => {
        updateStepCompletion(stepId, !currentStatus, comment);
    };

    const handleCommentChange = (stepId: number, isCompleted: boolean, comment: string) => {
        updateStepCompletion(stepId, isCompleted, comment);
    };

    const handleFinalize = async () => {
        if (!completion || !user) return;

        const requiredSteps = completion.sop.steps.filter(s => s.is_required);
        const completedRequiredSteps = completion.completion_steps.filter(cs => 
            cs.is_completed && completion.sop.steps.find(s => s.id === cs.step_id)?.is_required
        );

        if (requiredSteps.length !== completedRequiredSteps.length) {
            toast.error('All required steps must be completed before finalizing');
            return;
        }

        try {
            setIsSaving(true);
            
            await finalizeSopCompletion(completion.id, {
                signature,
                final_notes: finalNotes,
                technician_name: user.name || user.email
            });

            toast.success('SOP completed successfully!');
            navigateBack();
            
        } catch (error: unknown) {
            console.error('Error finalizing SOP:', error);
            
            // Type guard for axios errors
            const axiosError = error as { response?: { data?: { message?: string } } };
            toast.error(axiosError.response?.data?.message || 'Failed to finalize SOP');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!completion) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        SOP Completion Not Found
                    </h2>
                    <button
                        onClick={navigateBack}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                        Return to Job Cards
                    </button>
                </div>
            </div>
        );
    }

    const requiredStepsCount = completion.sop.steps.filter(s => s.is_required).length;
    const completedRequiredSteps = completion.completion_steps.filter(cs => 
        cs.is_completed && completion.sop.steps.find(s => s.id === cs.step_id)?.is_required
    ).length;
    const totalStepsCount = completion.sop.steps.length;
    const completedStepsCount = completion.completion_steps.filter(cs => cs.is_completed).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={navigateBack}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Complete SOP
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            {completion.sop.category} - {completion.sop.title}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                            KSA: {completion.ksa_number}
                        </p>
                        {/* Display job card information if available */}
                        {jobCardInfo && (
                            <div className="mt-2 space-y-1">
                                {jobCardInfo.jobNumber && (
                                    <p className="text-sm text-gray-500 dark:text-gray-500">
                                        Job Card: {jobCardInfo.jobNumber}
                                    </p>
                                )}
                                {jobCardInfo.jobcardStoreName && (
                                    <p className="text-sm text-gray-500 dark:text-gray-500">
                                        Store: {jobCardInfo.jobcardStoreName}
                                        {jobCardInfo.jobcardStoreNumber && ` (${jobCardInfo.jobcardStoreNumber})`}
                                    </p>
                                )}
                            </div>
                        )}
                        
                        {/* Display job information from database if available */}
                        {completion.job && (
                            <div className="mt-2 space-y-1">
                                <p className="text-sm text-gray-500 dark:text-gray-500">
                                    Task: {completion.job.description}
                                </p>
                                {completion.job.job_card?.job_number && (
                                    <p className="text-sm text-gray-500 dark:text-gray-500">
                                        Job Card: {completion.job.job_card.job_number}
                                    </p>
                                )}
                                {completion.job.job_card?.order_number && (
                                    <p className="text-sm text-gray-500 dark:text-gray-500">
                                        Order: {completion.job.job_card.order_number}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Progress Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Progress Overview
                        </h2>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            completion.status === 'completed' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                            {completion.status === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                                {completedStepsCount}/{totalStepsCount}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Steps</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {completedRequiredSteps}/{requiredStepsCount}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Required Steps</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {completion.sop.estimated_duration || '—'}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Est. Duration (min)</div>
                        </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <span>Overall Progress</span>
                            <span>{Math.round((completedStepsCount / totalStepsCount) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                                className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(completedStepsCount / totalStepsCount) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-4 mb-6">
                    {completion.sop.steps && completion.sop.steps.length > 0 ? (
                        completion.sop.steps.map((sopStep) => {
                            const completionStep = completion.completion_steps.find(cs => cs.step_id === sopStep.id);
                            if (!completionStep) {
                                return null;
                            }

                        return (
                            <div 
                                key={sopStep.id}
                                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 transition-all duration-200 ${
                                    completionStep.is_completed
                                        ? 'border-green-200 dark:border-green-800'
                                        : 'border-gray-200 dark:border-gray-700'
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    <button
                                        onClick={() => handleStepToggle(completionStep.id, completionStep.is_completed, completionStep.comment)}
                                        className="mt-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                        disabled={completion.status === 'completed'}
                                    >
                                        {completionStep.is_completed ? (
                                            <CheckCircle size={24} className="text-green-600 dark:text-green-400" />
                                        ) : (
                                            <Circle size={24} />
                                        )}
                                    </button>
                                    
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded">
                                                Step {sopStep.step_order}
                                            </span>
                                            {sopStep.is_required && (
                                                <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                                    Required
                                                </span>
                                            )}
                                            {sopStep.requires_photo && (
                                                <span title="Photo required">
                                                    <Camera size={16} className="text-gray-400" />
                                                </span>
                                            )}
                                            {sopStep.requires_comment && (
                                                <span title="Comment required">
                                                    <MessageSquare size={16} className="text-gray-400" />
                                                </span>
                                            )}
                                            {completionStep.completed_at && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                    <Clock size={12} />
                                                    {new Date(completionStep.completed_at).toLocaleTimeString()}
                                                </span>
                                            )}
                                        </div>
                                        
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                            {sopStep.step_text}
                                        </h3>
                                        
                                        {sopStep.instructions && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
                                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                                    <strong>Instructions:</strong> {sopStep.instructions}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {(sopStep.requires_comment || completionStep.comment) && (
                                            <div className="mt-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    {sopStep.requires_comment ? 'Comment (Required)' : 'Comment (Optional)'}
                                                </label>
                                                <textarea
                                                    value={completionStep.comment || ''}
                                                    onChange={(e) => handleCommentChange(completionStep.id, completionStep.is_completed, e.target.value)}
                                                    placeholder={sopStep.requires_comment ? 'Please add a comment...' : 'Add comment (optional)...'}
                                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                                                    rows={3}
                                                    disabled={completion.status === 'completed'}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                            })
                            ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
                            <p className="text-gray-500 dark:text-gray-400">No SOP steps found</p>
                        </div>
                    )}
                </div>

                {/* Finalization Section */}
                {completion.status === 'in_progress' && completedRequiredSteps === requiredStepsCount && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Ready to Complete SOP
                            </h3>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Signature (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={signature}
                                    onChange={(e) => setSignature(e.target.value)}
                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Enter your signature..."
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Final Notes (Optional)
                                </label>
                                <textarea
                                    value={finalNotes}
                                    onChange={(e) => setFinalNotes(e.target.value)}
                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                                    rows={4}
                                    placeholder="Add any final notes or observations..."
                                />
                            </div>
                            
                            <button
                                onClick={handleFinalize}
                                disabled={isSaving}
                                className="w-full px-4 py-3 text-sm font-medium text-white bg-green-600 dark:bg-green-500 rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isSaving ? 'Completing SOP...' : 'Complete SOP'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Completed Status */}
                {completion.status === 'completed' && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                            <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                                SOP Completed
                            </h3>
                        </div>
                        <p className="text-green-800 dark:text-green-200">
                            This SOP was completed on {new Date(completion.completed_at!).toLocaleString()}
                        </p>
                        {completion.duration_minutes && (
                            <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                                Duration: {completion.duration_minutes} minutes
                            </p>
                        )}
                        {completion.notes && (
                            <div className="mt-3">
                                <strong className="text-green-900 dark:text-green-100">Final Notes:</strong>
                                <p className="text-green-800 dark:text-green-200 mt-1">{completion.notes}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
